/**
 * SpecializedSections — renders report-type-specific sections for
 * Power Availability, Water Resource, Test Fit, and Interconnection reports.
 */
import React from 'react';
import { reportHeadingInk } from '../../../lib/reportSectionHeading';
import type { SiteRagReport, DataSource } from '../../../types/siteRag';
import type { ReportTheme } from '../../../types/reportTheme';
import { walkCitationText } from '../../../lib/walkCitationText';
import { findingClaimEntriesToStrings } from '../../../lib/findingClaim';
import { roleColor } from '../../../lib/reportRoleColors';
import { SectionQuarantineBanner } from './SectionQuarantineBanner';
import {
  EditorialSubsection,
  EditorialRecommendations,
  EditorialBadge,
} from './editorial';
import { EditorialSectionShell } from './EditorialSectionShell';
import { REPORT_SECTION_PAGE_COLS_2_CLASS } from '../../../lib/reportSectionChrome';

const MONO = 'var(--report-body-font, inherit)';

/**
 * W3 (legacy shell-wrap) — optional props that let each legacy specialized
 * aggregate participate in the W2 column engine + per-section chrome WHEN (and
 * only when) a theme preset is in play. Threaded from SiteRagReportView; ALL
 * default to the legacy no-op so the `!hasModernSections` + no-preset path
 * stays byte-identical (the gate is `presetActive`).
 */
interface ShellProps {
  /** True ONLY when `presentation.presetId != null`. Gates every wrap. */
  presetActive?: boolean;
  /** Resolved body page-column count for this aggregate (1 | 2). */
  pageColumns?: 1 | 2;
  /** Per-section shell padding flag (mirrors inlineSectionShellPadding). */
  shellPadding?: boolean;
  /** Whether the left confidential rail is omitted (mirrors omitSectionSidebar). */
  omitSectionSidebar?: boolean;
}

interface Props extends ShellProps {
  report: SiteRagReport;
  theme: ReportTheme;
}

/**
 * W3 — shared gated wrapper. Returns `body` untouched when no preset is active
 * (byte-stable legacy path); otherwise wraps it in EditorialSectionShell (classic
 * mode) so the aggregate inherits padding/density/typography + the column engine.
 */
function withGatedShell(
  body: React.ReactElement,
  { theme, presetActive, pageColumns = 1, shellPadding = false, omitSectionSidebar = true }: {
    theme: ReportTheme;
  } & ShellProps,
): React.ReactElement {
  if (!presetActive) return body;
  return (
    <EditorialSectionShell
      theme={theme}
      shellPadding={shellPadding}
      omitSectionSidebar={omitSectionSidebar}
      data-section-columns={String(pageColumns)}
      extraClassName={pageColumns === 2 ? REPORT_SECTION_PAGE_COLS_2_CLASS : undefined}
    >
      {body}
    </EditorialSectionShell>
  );
}

// ── TS3 (headingScale) + TS8 (themeTypographyScale) ─────────────────
// Derived INLINE here (no cross-cluster helper import). `headingScale`
// picks base h2/h3 rem sizes; `themeTypographyScale` (0.875|1|1.125|
// 1.25) multiplies them. Legacy themes missing either field fall
// through to the editorial baseline / 1× so existing reports render
// unchanged. Applied as inline `fontSize` on the <h2>/<h3> (which keep
// their text-xl / text-sm classes) so size tracks the active theme.
const SPECIALIZED_HEADING_REM: Record<
  'editorial' | 'technical' | 'compact',
  { h2: number; h3: number }
> = {
  editorial: { h2: 1.5, h3: 0.875 },
  technical: { h2: 1.25, h3: 0.8 },
  compact: { h2: 1.05, h3: 0.75 },
};

function specializedTypographyMult(scale: number | undefined): number {
  return typeof scale === 'number' && Number.isFinite(scale) && scale > 0
    ? scale
    : 1;
}

function specializedHeadingSizes(theme: ReportTheme): { h2: string; h3: string } {
  const scale =
    theme.headingScale === 'technical' || theme.headingScale === 'compact'
      ? theme.headingScale
      : 'editorial';
  const base = SPECIALIZED_HEADING_REM[scale];
  const mult = specializedTypographyMult(theme.themeTypographyScale);
  const rem = (n: number) => `${Math.round(n * mult * 1000) / 1000}rem`;
  return { h2: rem(base.h2), h3: rem(base.h3) };
}

/** TS8 — body text size (1rem baseline × typography multiplier). */
function specializedBodyFontSize(theme: ReportTheme): string {
  const mult = specializedTypographyMult(theme.themeTypographyScale);
  return `${Math.round(mult * 1000) / 1000}rem`;
}

// ── TS2 (tableStyle, web) — now theme-toned via roleColor ───────────
// The active theme's `tableStyle` drives the per-row treatment, but the
// concrete colours come from roleColor(theme,…) rather than literal
// slate classes — so a warm-paper / cool-white preset gets matching
// zebra + hairline tones. Defaults to `lined` (the prior look) so
// themes without the field render the same structural hairline.
//   - striped:    zebra rows (roleColor 'subtleBg'), no per-row border
//   - lined:      thin bottom hairline per row (roleColor 'hair')
//   - borderless: no row borders / striping
//
// `rowStyle(i)` returns the per-row inline style for the resolved style;
// `headStyle` is the header underline (replaces border-b-2 border-slate-900).
type SpecializedTableStyle = {
  rowStyle: (i: number) => React.CSSProperties;
  headStyle: React.CSSProperties;
};

function specializedTableStyle(theme: ReportTheme): SpecializedTableStyle {
  const hair = roleColor(theme, 'hair');
  const subtle = roleColor(theme, 'subtleBg');
  const headRule = roleColor(theme, 'rule');
  const headStyle: React.CSSProperties = { borderBottom: `2px solid ${headRule}` };
  switch (theme.tableStyle) {
    case 'striped':
      return {
        rowStyle: (i) => (i % 2 === 1 ? { background: subtle } : {}),
        headStyle,
      };
    case 'borderless':
      return { rowStyle: () => ({}), headStyle };
    case 'lined':
    default:
      return { rowStyle: () => ({ borderBottom: `1px solid ${hair}` }), headStyle };
  }
}

// Table header cell — small-caps mono in the muted tone (replaces the
// literal text-text-muted + tracking-widest utility chain).
function tableHeadCellStyle(theme: ReportTheme): React.CSSProperties {
  return {
    fontFamily: MONO,
    letterSpacing: '0.12em',
    color: roleColor(theme, 'muted'),
  };
}

const heading = (text: string, theme: ReportTheme) => (
  <h2 className="text-xl font-bold border-b-2 pb-2 mb-6" style={{ borderColor: reportHeadingInk(theme), color: reportHeadingInk(theme), fontFamily: theme.headingFont, fontSize: specializedHeadingSizes(theme).h2 }}>
    {text}
  </h2>
);

// Small-caps mono label (theme ink) — used where a bare label is needed
// without the hairline-topped subsection wrapper (e.g. inside a card's
// definition grid). Replaces the old text-text-secondary <h3>.
const sub = (text: string, theme?: ReportTheme) => (
  <h3
    className="font-bold uppercase mb-2"
    style={{
      fontFamily: MONO,
      letterSpacing: '0.14em',
      color: roleColor(theme, 'ink'),
      fontSize: theme ? specializedHeadingSizes(theme).h3 : undefined,
    }}
  >
    {text}
  </h3>
);

// Citation walker is the only reason these helpers grew a `refs` arg —
// inline narrative text can carry [Ref N] tokens that we render as
// clickable RefLink pills. Pass the report's references[] in from the
// component (each section component has it via props.report).
const body = (text: string | undefined, refs?: DataSource[], theme?: ReportTheme) =>
  text ? (
    <p className="text-sm leading-relaxed mb-4" style={{ color: roleColor(theme, 'body') }}>
      {walkCitationText(text, refs)}
    </p>
  ) : null;

// Editorial sub-section: hairline-TOPPED prose block with a small-caps
// label (replaces the prior bare <h3> + <p> pair). Renders nothing when
// the prose is empty so we don't print an orphan hairline label.
const subsection = (label: string, text: string | undefined, theme: ReportTheme, refs?: DataSource[]) => {
  if (!text) return null;
  return (
    <EditorialSubsection theme={theme} label={label} className="mt-4">
      {walkCitationText(text, refs)}
    </EditorialSubsection>
  );
};

// Recommendations → editorial numbered list with an accent left-rule.
const recommendationList = (items: string[] | undefined, theme: ReportTheme, refs?: DataSource[]) => {
  if (!items?.length) return null;
  return (
    <EditorialRecommendations
      theme={theme}
      className="mt-4"
      items={items.map((item) => walkCitationText(item, refs))}
    />
  );
};

// Risks → small-caps 'Risks' head + dotted list in the NEGATIVE tone
// (replaces the prior text-danger / bg-danger literals).
const riskList = (items: string[] | undefined, refs?: DataSource[], theme?: ReportTheme) => {
  if (!items?.length) return null;
  return (
    <div className="mt-4">
      {sub('Risks', theme)}
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li
            key={`item-${i}`}
            className="flex items-start gap-2 text-sm"
            style={{ color: roleColor(theme, 'negative') }}
          >
            <span
              className="w-1.5 h-1.5 mt-1.5 shrink-0 rounded-full"
              style={{ background: roleColor(theme, 'negative') }}
            />
            {walkCitationText(item, refs)}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const PowerSections: React.FC<Props> = ({ report, theme, presetActive, pageColumns, shellPadding, omitSectionSidebar }) => {
  const pa = report.powerAnalysis;
  const refs = report.references;
  // TS8 — body size for this section's narrative text (h2/h3 carry
  // their own inline fontSize; this scales the inherited body text).
  const bodyFontSize = specializedBodyFontSize(theme);
  // TS2 — theme-toned row/header styles for the report tables.
  const tableStyle = specializedTableStyle(theme);
  if (!pa) return null;
  const tree = (
    <div className="space-y-8" style={{ fontFamily: theme.bodyFont, color: roleColor(theme, 'body'), fontSize: bodyFontSize }}>
      {heading('Power Availability Analysis', theme)}
      {/* SNT-3 — quarantine banner for power-analysis substation field. */}
      <SectionQuarantineBanner
        sectionId="powerAnalysis.substationAssessment"
        sectionLabel="Substation Assessment"
        guardStatus={report.sectionGuardStatus?.['powerAnalysis.substationAssessment']}
        quarantinedOriginal={report.quarantinedOriginal?.['powerAnalysis.substationAssessment']}
        renderedText={pa.substationAssessment}
      />
      {subsection('Substation Assessment', pa.substationAssessment, theme, refs)}
      {subsection('Interconnection Queue', pa.interconnectionQueue, theme, refs)}
      {subsection('Capacity Analysis', pa.capacityAnalysis, theme, refs)}
      {subsection('Curtailment Risk', pa.curtailmentRisk, theme, refs)}
      {subsection('Rate Structure', pa.rateStructure, theme, refs)}
      {subsection('Renewable Integration', pa.renewableIntegration, theme, refs)}
      {subsection('Redundancy Assessment', pa.redundancyAssessment, theme, refs)}
      {recommendationList(pa.recommendations, theme, refs)}
      {riskList(findingClaimEntriesToStrings(pa.risks), refs, theme)}

      {/* BTM Detailed Analysis — hairline-topped editorial blocks per technology */}
      {report.btmDetailedAnalysis && report.btmDetailedAnalysis.length > 0 && (
        <div>
          {heading('Behind-the-Meter Technology Analysis', theme)}
          <div className="space-y-8">
            {report.btmDetailedAnalysis.map((btm, i) => (
              <div
                key={`item-${i}`}
                style={{ borderTop: `2px solid ${roleColor(theme, 'rule')}`, paddingTop: 14 }}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3
                    className="text-lg font-bold"
                    style={{ color: roleColor(theme, 'ink'), fontFamily: theme.headingFont }}
                  >
                    {btm.technology}
                  </h3>
                  <EditorialBadge theme={theme} tone="accent">
                    {btm.lcoeRange}/MWh
                  </EditorialBadge>
                </div>
                <dl className="grid grid-cols-2 lg:grid-cols-3 gap-3 text-sm mt-3">
                  {[
                    ['Capital Cost', btm.capitalCost],
                    ['Timeline', btm.timelineToFirstPower],
                    ['Land', btm.landRequirement],
                    ['Carbon', btm.carbonIntensity],
                    ['Operating Model', btm.operatingModel],
                    ['Financial', btm.financialStructure],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt
                        className="font-bold uppercase text-xs"
                        style={{ fontFamily: MONO, letterSpacing: '0.12em', color: roleColor(theme, 'muted') }}
                      >
                        {label}
                      </dt>
                      <dd className="mt-0.5" style={{ color: roleColor(theme, 'body') }}>{value}</dd>
                    </div>
                  ))}
                </dl>
                {subsection('Regulatory Pathway', btm.regulatoryPathway, theme, refs)}
                {subsection('Site-Specific Factors', btm.siteSpecificFactors, theme, refs)}
                {subsection('Recommendation', btm.recommendation, theme, refs)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grid vs BTM Comparison */}
      {report.gridVsBtmComparison && report.gridVsBtmComparison.length > 0 && (
        <div>
          {heading('Grid vs BTM Comparison', theme)}
          {/* data-span-columns: defensive — wide comparison table spans both
              flow columns under a 2-col preset (legacy aggregate path). */}
          <div data-span-columns className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-table-style={theme.tableStyle ?? 'lined'}>
            <thead>
              <tr style={tableStyle.headStyle}>
                <th className="text-left py-2 pr-2 font-bold uppercase" style={tableHeadCellStyle(theme)}>Metric</th>
                <th className="text-left py-2 px-2 font-bold uppercase" style={tableHeadCellStyle(theme)}>Grid</th>
                <th className="text-left py-2 px-2 font-bold uppercase" style={tableHeadCellStyle(theme)}>BTM</th>
                <th className="text-left py-2 pl-2 font-bold uppercase" style={tableHeadCellStyle(theme)}>Advantage</th>
              </tr>
            </thead>
            <tbody>
              {report.gridVsBtmComparison.map((row, i) => (
                <tr key={`item-${i}`} style={tableStyle.rowStyle(i)}>
                  <td className="py-2 pr-2 font-bold" style={{ color: roleColor(theme, 'ink') }}>{row.metric}</td>
                  <td className="py-2 px-2" style={{ color: roleColor(theme, 'body') }}>{row.gridOption}</td>
                  <td className="py-2 px-2" style={{ color: roleColor(theme, 'body') }}>{row.btmOption}</td>
                  <td className="py-2 pl-2">
                    <EditorialBadge
                      theme={theme}
                      variant="outline"
                      tone={row.advantage === 'BTM' ? 'positive' : row.advantage === 'Grid' ? 'warning' : 'neutral'}
                    >
                      {row.advantage}
                    </EditorialBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* LCOE Detailed Analysis */}
      {report.lcoeDetailedAnalysis && report.lcoeDetailedAnalysis.length > 0 && (
        <div>
          {heading('LCOE Detailed Breakdown', theme)}
          {/* data-span-columns: defensive — wide LCOE breakdown table spans both
              flow columns under a 2-col preset (legacy aggregate path). */}
          <div data-span-columns className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-table-style={theme.tableStyle ?? 'lined'}>
            <thead>
              <tr style={tableStyle.headStyle}>
                <th className="text-left py-2 pr-2 font-bold uppercase" style={tableHeadCellStyle(theme)}>Technology</th>
                <th className="text-left py-2 px-2 font-bold uppercase" style={tableHeadCellStyle(theme)}>CAPEX/MW</th>
                <th className="text-left py-2 px-2 font-bold uppercase" style={tableHeadCellStyle(theme)}>Fixed O&M</th>
                <th className="text-left py-2 px-2 font-bold uppercase" style={tableHeadCellStyle(theme)}>Var O&M</th>
                <th className="text-left py-2 px-2 font-bold uppercase" style={tableHeadCellStyle(theme)}>Fuel</th>
                <th className="text-left py-2 px-2 font-bold uppercase" style={tableHeadCellStyle(theme)}>CF</th>
                <th className="text-left py-2 px-2 font-bold uppercase" style={tableHeadCellStyle(theme)}>LCOE</th>
                <th className="text-left py-2 pl-2 font-bold uppercase" style={tableHeadCellStyle(theme)}>Adjustments</th>
              </tr>
            </thead>
            <tbody>
              {report.lcoeDetailedAnalysis.map((row, i) => (
                <tr key={`item-${i}`} style={tableStyle.rowStyle(i)}>
                  <td className="py-2 pr-2 font-bold" style={{ color: roleColor(theme, 'ink') }}>{row.technology}</td>
                  <td className="py-2 px-2" style={{ fontFamily: MONO, color: roleColor(theme, 'body') }}>{row.capitalCostPerMW}</td>
                  <td className="py-2 px-2" style={{ fontFamily: MONO, color: roleColor(theme, 'body') }}>{row.fixedOM}</td>
                  <td className="py-2 px-2" style={{ fontFamily: MONO, color: roleColor(theme, 'body') }}>{row.variableOM}</td>
                  <td className="py-2 px-2" style={{ fontFamily: MONO, color: roleColor(theme, 'body') }}>{row.fuelCost}</td>
                  <td className="py-2 px-2" style={{ fontFamily: MONO, color: roleColor(theme, 'body') }}>{row.capacityFactor}</td>
                  <td className="py-2 px-2 font-bold" style={{ fontFamily: MONO, color: roleColor(theme, 'ink') }}>{row.lcoeResult}</td>
                  <td className="py-2 pl-2 text-xs" style={{ color: roleColor(theme, 'muted') }}>{row.siteAdjustments}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
  return withGatedShell(tree, { theme, presetActive, pageColumns, shellPadding, omitSectionSidebar });
};

export const WaterSections: React.FC<Props> = ({ report, theme, presetActive, pageColumns, shellPadding, omitSectionSidebar }) => {
  const wa = report.waterAnalysis;
  const refs = report.references;
  const bodyFontSize = specializedBodyFontSize(theme);
  if (!wa) return null;
  const tree = (
    <div className="space-y-8" style={{ fontFamily: theme.bodyFont, color: roleColor(theme, 'body'), fontSize: bodyFontSize }}>
      {heading('Water Resource Assessment', theme)}
      {/* SNT-3 — quarantine banner for water-analysis supply-availability field. */}
      <SectionQuarantineBanner
        sectionId="waterAnalysis.supplyAvailability"
        sectionLabel="Supply Availability"
        guardStatus={report.sectionGuardStatus?.['waterAnalysis.supplyAvailability']}
        quarantinedOriginal={report.quarantinedOriginal?.['waterAnalysis.supplyAvailability']}
        renderedText={wa.supplyAvailability}
      />
      {subsection('Supply Availability', wa.supplyAvailability, theme, refs)}
      {subsection('Groundwater Assessment', wa.groundwaterAssessment, theme, refs)}
      {subsection('Surface Water Assessment', wa.surfaceWaterAssessment, theme, refs)}
      {subsection('Water Rights Analysis', wa.waterRightsAnalysis, theme, refs)}
      {subsection('Drought Resilience', wa.droughtResilience, theme, refs)}
      {subsection('Cooling Water Requirements', wa.coolingWaterRequirements, theme, refs)}
      {subsection('Wastewater Management', wa.wastewaterManagement, theme, refs)}
      {subsection('Water Conservation', wa.waterConservation, theme, refs)}
      {recommendationList(wa.recommendations, theme, refs)}
      {riskList(findingClaimEntriesToStrings(wa.risks), refs, theme)}
    </div>
  );
  return withGatedShell(tree, { theme, presetActive, pageColumns, shellPadding, omitSectionSidebar });
};

export const TestFitSections: React.FC<Props> = ({ report, theme, presetActive, pageColumns, shellPadding, omitSectionSidebar }) => {
  const tf = report.testFitAnalysis;
  const refs = report.references;
  const bodyFontSize = specializedBodyFontSize(theme);
  if (!tf) return null;
  const tree = (
    <div className="space-y-8" style={{ fontFamily: theme.bodyFont, color: roleColor(theme, 'body'), fontSize: bodyFontSize }}>
      {heading('Test Fit / Site Layout Analysis', theme)}
      {/* SNT-3 — quarantine banner for testFit site-constraints field. */}
      <SectionQuarantineBanner
        sectionId="testFitAnalysis.siteConstraints"
        sectionLabel="Site Constraints"
        guardStatus={report.sectionGuardStatus?.['testFitAnalysis.siteConstraints']}
        quarantinedOriginal={report.quarantinedOriginal?.['testFitAnalysis.siteConstraints']}
        renderedText={tf.siteConstraints}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4">
        <EditorialSubsection theme={theme} label="Site Constraints">
          {body(tf.siteConstraints, refs, theme)}
        </EditorialSubsection>
        <EditorialSubsection theme={theme} label="Building Placement">
          {body(tf.buildingPlacement, refs, theme)}
        </EditorialSubsection>
        <EditorialSubsection theme={theme} label="Setback Compliance">
          {body(tf.setbackCompliance, refs, theme)}
        </EditorialSubsection>
        <EditorialSubsection theme={theme} label="Parking & Access">
          {body(tf.parkingAccess, refs, theme)}
        </EditorialSubsection>
      </div>
      {subsection('Stormwater Detention', tf.stormwaterDetention, theme, refs)}
      {subsection('Utility Routing', tf.utilityRouting, theme, refs)}
      {subsection('Data Hall Orientation', tf.dataHallOrientation, theme, refs)}
      {subsection('Phased Layout', tf.phaseLayout, theme, refs)}
      {recommendationList(tf.recommendations, theme, refs)}
    </div>
  );
  return withGatedShell(tree, { theme, presetActive, pageColumns, shellPadding, omitSectionSidebar });
};

export const InterconnectionSections: React.FC<Props> = ({ report, theme, presetActive, pageColumns, shellPadding, omitSectionSidebar }) => {
  const ia = report.interconnectionAnalysis;
  const refs = report.references;
  const bodyFontSize = specializedBodyFontSize(theme);
  if (!ia) return null;
  const tree = (
    <div className="space-y-8" style={{ fontFamily: theme.bodyFont, color: roleColor(theme, 'body'), fontSize: bodyFontSize }}>
      {heading('Interconnection Feasibility Study', theme)}
      {/* SNT-3 — quarantine banner for interconnection fiber-availability field. */}
      <SectionQuarantineBanner
        sectionId="interconnectionAnalysis.fiberAvailability"
        sectionLabel="Fiber Availability"
        guardStatus={report.sectionGuardStatus?.['interconnectionAnalysis.fiberAvailability']}
        quarantinedOriginal={report.quarantinedOriginal?.['interconnectionAnalysis.fiberAvailability']}
        renderedText={ia.fiberAvailability}
      />
      {subsection('Fiber Availability', ia.fiberAvailability, theme, refs)}
      {subsection('Latency Modeling', ia.latencyModeling, theme, refs)}
      {subsection('Carrier Diversity', ia.carrierDiversity, theme, refs)}
      {subsection('Internet Exchange Proximity', ia.ixProximity, theme, refs)}
      {subsection('Submarine Cable Access', ia.submarineCable, theme, refs)}
      {subsection('Network Resilience', ia.networkResilience, theme, refs)}
      {subsection('Edge Deployment Suitability', ia.edgeDeployment, theme, refs)}
      {recommendationList(ia.recommendations, theme, refs)}
      {riskList(ia.risks, refs, theme)}
    </div>
  );
  return withGatedShell(tree, { theme, presetActive, pageColumns, shellPadding, omitSectionSidebar });
};
